export type ProxyEnv = 'prod' | 'pre' | 'test' | 'idc';

// 环境标识到URL的映射（对内）
export const internalEnvToUrlMap: Record<ProxyEnv, string> = {
  prod: 'https://codeflicker.corp.kuaishou.com',
  pre: 'https://pre-kinsight.test.gifshow.com',
  test: 'https://codeflicker.staging.kuaishou.com',
  idc: 'https://kinsight.corp.kuaishou.com',
};

// 环境标识到URL的映射（对外）
export const externalEnvToUrlMap: Record<ProxyEnv, string> = {
  prod: 'https://www.codeflicker.ai',
  pre: 'https://codeflicker-toc.test.gifshow.com',
  test: 'https://kwaipilot-ide.staging.kuaishou.com',
  idc: 'https://kwaipilot-ide.staging.kuaishou.com',
};

/**
 * 对内web端直接获取SSO登录URL
 * @returns SSO登录URL
 */
export function generateInternalSSOUrl(redirect?: string): string {
  const proxyUrl = window.location.origin;
  const SSO_URL = 'https://sso.corp.kuaishou.com/cas/login?service=';
  const redirectParam = redirect === '/' ? 'index' : redirect;
  const service =
    proxyUrl.replace('https', 'http') +
    `/api/auth/sso/callback?redirect=${encodeURIComponent(redirectParam || '/dashboard')}`;
  const encodeParam = encodeURIComponent(service);
  return SSO_URL + encodeParam;
}

/**
 * 根据当前域名获取对应的内部URL
 * @returns 内部URL或null
 */
export function getInternalUrlByCurrentDomain(): string | null {
  const currentHostname = window.location.hostname;

  // 遍历外部URL映射，查找匹配的域名
  for (const [env, externalUrl] of Object.entries(externalEnvToUrlMap) as [ProxyEnv, string][]) {
    const url = new URL(externalUrl);
    if (currentHostname === url.hostname || currentHostname.endsWith(`.${url.hostname}`)) {
      if (env === 'prod') {
        return internalEnvToUrlMap.prod;
      }
      // env !== prod 时，取 URL query 里的 env；没有则用 pre
      const queryEnv = new URLSearchParams(window.location.search).get('env');
      const resolvedEnv: ProxyEnv =
        queryEnv && Object.keys(internalEnvToUrlMap).includes(queryEnv)
          ? (queryEnv as ProxyEnv)
          : 'pre';
      return internalEnvToUrlMap[resolvedEnv];
    }
  }

  // 默认返回prod环境的内部URL
  return internalEnvToUrlMap.prod;
}

/**
 * 获取SSO登录URL
 * @returns SSO登录URL
 */
export function generateSSOLoginUrl(): string {
  const proxyUrl = getInternalUrlByCurrentDomain() || internalEnvToUrlMap.prod;
  const SSO_URL = 'https://sso.corp.kuaishou.com/cas/login?service=';
  const service = proxyUrl.replace('https', 'http') + '/api/auth/sso/callback?redirect=';
  const encodeParam = encodeURIComponent(service) + '/';
  return SSO_URL + encodeParam;
}

/**
 * 生成带邮箱的SSO登录URL
 * @param email 用户邮箱，示例 zhangsan
 * @returns SSO登录URL（带邮箱和uuid参数）
 */
export function generateSSOLoginUrlWithEmail(email: string): string {
  const proxyUrl = getInternalUrlByCurrentDomain() || internalEnvToUrlMap.prod;
  const SSO_URL = 'https://sso.corp.kuaishou.com/cas/login?service=';
  const service =
    proxyUrl.replace('https', 'http') + `/api/auth/sso/callback?email=${email}&redirect=`;
  const encodeParam = encodeURIComponent(service) + '/';
  return SSO_URL + encodeParam;
}

/**
 * 判断邮箱是否为快手邮箱
 * @param email 用户邮箱，示例 zhangsan
 * @returns 是否为快手邮箱
 */
export function getIsKuaishouEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith('@kuaishou.com');
}

/**
 * 判断是否是内部域名
 * @returns 如果是 internalEnvToUrlMap 中存在的域名或 localhost 返回 true，否则返回 false
 */
export const isInternalDomain = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentHostname = window.location.hostname;

  // 判断是否为 localhost
  if (currentHostname === 'localhost') {
    return true;
  }

  // 提取内部域名映射中的所有 hostname
  const internalHostnames = Object.values(internalEnvToUrlMap).map((url) => {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
  });

  // 判断当前 hostname 是否匹配内部域名
  return internalHostnames.includes(currentHostname);
};
